import React, { useEffect } from 'react';
import { Overlay, ModalWrapper, Container, CloseIcon, Title, Message, SubTitle, TextContainer, HeaderWrapper, RequestClassChangeButton, StudentBox } from './style';
import PurpleBackgroundBtn from '../Buttons/PurpleBackgroundBtn';
import closeicon from '../../static/icons/close-icon.svg';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getClasses, getStudentsForClass } from '../../service';
import { StudentChip } from '../StudentChip/index.jsx';
import { Avatar } from '@boringer-avatars/react';

// This component is used to display the verify classes popup
const VerifyClassesPopup = ({ setShowVerifyClassesPopup, onOpenClassChangeRequest, setShowMarkingPreferencesPopup, onConfirm }) => {

    // null = nothing selected yet; the effect below picks the first class once loaded.
    // Was '1' — a hardcoded string that never matched a real class id, so the
    // auto-select effect could never fire and the students panel rendered "No class selected".
    const [selectedClassId, setSelectedClassId] = React.useState(null);

    // Fetch classes
    const classesQuery = useQuery({
        queryKey: ["classes"],
        queryFn: getClasses,
        staleTime: 3600000,
    });

    // Fetch students and attach to classes
    const studentsQuery = useQuery({
        queryKey: ["classes-with-students"],
        queryFn: async () => {
            if (!classesQuery.data) return [];

            const classesWithStudents = await Promise.all(
                classesQuery.data.map(async (cls) => {
                    const students = await getStudentsForClass(cls.id);
                    return {
                        ...cls,
                        students,
                    };
                })
            );

            return classesWithStudents;
        },
        enabled: !!classesQuery.data,
    });

    // Set default selected class once classes load.
    useEffect(() => {
        if (classesQuery.data?.length && !selectedClassId) {
            setSelectedClassId(classesQuery.data[0].id);
        }
    }, [classesQuery.data, selectedClassId]);

    // Confirm path = user explicitly clicked "Looks good!" and we mark the
    // task done + advance them to marking prefs.
    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        setShowVerifyClassesPopup(false);
        setShowMarkingPreferencesPopup(true);
    };

    // Close path = user dismissed (X, backdrop). Don't auto-advance the flow
    // or mark the task complete. Letting close act like confirm was confusing.
    const handleClose = () => {
        setShowVerifyClassesPopup(false);
    };

    const handleClassChangeRequest = () => {
        if (onOpenClassChangeRequest) {
            onOpenClassChangeRequest();
        } else {
            setShowVerifyClassesPopup(false);
        }
    };

    // Check errors BEFORE loading. A rejected classesQuery leaves the dependent
    // studentsQuery disabled, which still reports isLoading=true in React Query
    // v4 — so a loading-first guard would mask the error and hang on "Loading...".
    // (Previously a rejected fetch rendered an empty/partial panel with no signal.)
    if (classesQuery.isError || studentsQuery.isError) {
        return (
            <Overlay onClick={handleClose}>
                <ModalWrapper onClick={(e) => e.stopPropagation()}>
                    <Container>
                        <HeaderWrapper>
                            <TextContainer>
                                <Title>We couldn't load your classes.</Title>
                                <Message>Please check your connection and try again.</Message>
                            </TextContainer>
                            <CloseIcon src={closeicon} alt="close-icon" onClick={handleClose} />
                        </HeaderWrapper>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <PurpleBackgroundBtn
                                buttonText="Retry"
                                onClickFn={() => { classesQuery.refetch(); studentsQuery.refetch(); }}
                            />
                        </div>
                    </Container>
                </ModalWrapper>
            </Overlay>
        );
    }

    if (classesQuery.isLoading || studentsQuery.isLoading) {
        return <div>Loading...</div>;
    }

    const selectedClass = studentsQuery.data?.find(
        (cls) => cls.id === selectedClassId
    );

    return (
        <Overlay onClick={handleClose}>
            <ModalWrapper onClick={(e) => e.stopPropagation()}>
                <Container>
                    <HeaderWrapper>
                        <TextContainer>
                            <Title>
                                These are your classes and students with JeddAI accounts.
                            </Title>

                            <Message>
                                You can request a class change any time if there are class movements
                                or students are missing.
                            </Message>
                        </TextContainer>
                        <CloseIcon src={closeicon} alt="close-icon" onClick={handleClose} />
                    </HeaderWrapper>

                    <SubTitle>Classes</SubTitle>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            marginBottom: "5px",
                        }}
                    >
                        {studentsQuery.data?.map((cls) => (
                            <Box
                                key={cls.id}
                                onClick={() => setSelectedClassId(cls.id)}
                                sx={{
                                    border:
                                        selectedClassId === cls.id
                                            ? "2px solid #7200E0"
                                            : "1px solid #ccc",
                                    borderRadius: "30px",
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    backgroundColor:
                                        selectedClassId === cls.id ? "#FBF7FE" : "white",
                                    minWidth: "120px",
                                    textAlign: "center",
                                    fontWeight: selectedClassId === cls.id ? 600 : 400,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    width: "fit-content",
                                    height: "40px",
                                    fontFamily: 'IBM Plex Sans',
                                    color: selectedClassId === cls.id ? "#7200E0" : "#000000",
                                }}
                            >
                                {cls.title}
                            </Box>
                        ))}
                    </Box>

                    {/* STUDENTS SECTION */}
                    <Box
                        sx={{
                            border: "1px solid #ccc",
                            borderRadius: "12px",
                            padding: "20px",
                            backgroundColor: "#FBFBFB",
                            minHeight: "120px",
                        }}
                    >
                        <SubTitle>Students</SubTitle>

                        {selectedClass ? (
                            <Box
                                sx={{
                                    marginTop: "20px",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                    fontWeight: '400',
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    fontFamily: 'IBM Plex Sans',
                                    padding: "0px",
                                    borderRadius: "8px"

                                }}
                            >
                                {selectedClass.students?.length ? (
                                    selectedClass.students.map((student) => (

                                        <StudentBox key={student.id}>
                                            <div style={{ display: "flex", alignItems: "center",marginTop:'2.3px' }}>
                                                <Avatar
                                                    size={25}
                                                    variant="beam"
                                                    name={student.name}
                                                />
                                            </div>
                                            <span>{student.name}</span>
                                        </StudentBox>
                                    ))
                                ) : (
                                    <div>No students found</div>
                                )}
                            </Box>
                        ) : (
                            <div>No class selected</div>
                        )}
                    </Box>

                    { /*Class change request */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "2px"
                    }}>
                        <RequestClassChangeButton onClick={handleClassChangeRequest} >

                            Request class change
                        </RequestClassChangeButton>
                        <PurpleBackgroundBtn
                            buttonText="Looks good!"
                            onClickFn={handleConfirm}
                        />
                    </Box>
                </Container>
            </ModalWrapper>
        </Overlay>
    );
};

export default VerifyClassesPopup;
